/**
 * Hook for local SQLite database operations
 * 
 * Provides convenient functions to work with local data
 * including products, orders, and consumptions.
 */

import { useCallback } from 'react';
import { useSQLiteContext } from '@/contexts/SQLiteContext';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import { addToSyncQueue } from '@/lib/sqlite-local';

// ============ Type Definitions ============

export interface LocalProduct {
  id: string;
  restaurant_id: string;
  name: string;
  price: number;
  category_id: string | null;
  image_url: string | null;
  description: string | null;
  is_active: number;
  tracks_stock: number;
  current_stock: number | null;
  cost_per_unit: number | null;
  barcode: string | null;
  production_location_id: string | null;
  available_pdv: number;
  available_table: number;
  available_tablet: number;
  is_featured: number;
  sort_order: number;
  synced_at: string | null;
}

export interface LocalCategory {
  id: string;
  restaurant_id: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  sort_order: number;
  is_active: number;
  synced_at: string | null;
}

export interface LocalTable {
  id: string;
  restaurant_id: string;
  number: string;
  name: string | null;
  status: string;
  current_order_id: string | null;
  grouped_to_order_id: string | null;
  service_tax_enabled: number;
  capacity: number;
  is_active: number;
  synced_at: string | null;
}

export interface LocalCommand {
  id: string;
  restaurant_id: string;
  number: string;
  status: string;
  current_order_id: string | null;
  grouped_to_order_id: string | null;
  customer_name: string | null;
  service_tax_enabled: number;
  is_active: number;
  synced_at: string | null;
}

export interface LocalOrder {
  id: string;
  restaurant_id: string;
  order_number: number | null;
  order_type: string | null;
  table_number: string | null;
  tab_number: string | null;
  table_id: string | null;
  command_id: string | null;
  customer_name: string | null;
  subtotal: number;
  discount: number;
  total: number;
  status: string;
  created_at: string;
  synced: number;
  server_id: string | null;
}

export interface LocalOrderItem {
  id: string;
  order_id: string;
  product_id: string | null;
  combo_id: string | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes: string | null;
  status: string;
  sent_to_kitchen: number;
  created_at: string;
  synced: number;
  server_id: string | null;
}

export interface LocalPaymentMethod {
  id: string;
  restaurant_id: string;
  name: string;
  type: string | null;
  fee_percentage: number;
  is_active: number;
  accepts_change: number;
  synced_at: string | null;
}

export interface LocalOrderPayment {
  id: string;
  order_id: string;
  payment_method_id: string;
  payment_method_name: string;
  amount: number;
  fee_amount: number;
  created_at: string;
  synced: number;
  server_id: string | null;
}

export interface LocalProductionLocation {
  id: string;
  restaurant_id: string;
  name: string;
  printer_name: string | null;
  is_active: number;
  synced_at: string | null;
}

// ============ Hook ============

export function useSQLiteLocal() {
  const { isReady, query, execute, transaction } = useSQLiteContext();
  const { activeRestaurant } = useRestaurantContext();
  const restaurantId = activeRestaurant?.id;

  // ============ Products ============

  const getProducts = useCallback((): LocalProduct[] => {
    if (!isReady || !restaurantId) return [];
    return query<LocalProduct>(
      `SELECT * FROM products WHERE restaurant_id = ? AND is_active = 1 ORDER BY sort_order, name`,
      [restaurantId]
    );
  }, [isReady, query, restaurantId]);

  const getProductById = useCallback((id: string): LocalProduct | null => {
    if (!isReady) return null;
    const results = query<LocalProduct>(`SELECT * FROM products WHERE id = ?`, [id]);
    return results[0] || null;
  }, [isReady, query]);

  const getProductByBarcode = useCallback((barcode: string): LocalProduct | null => {
    if (!isReady || !restaurantId) return null;
    const results = query<LocalProduct>(
      `SELECT * FROM products WHERE restaurant_id = ? AND barcode = ? AND is_active = 1`,
      [restaurantId, barcode]
    );
    return results[0] || null;
  }, [isReady, query, restaurantId]);

  const upsertProducts = useCallback((products: Partial<LocalProduct>[]): void => {
    if (!isReady || products.length === 0) return;
    
    const now = new Date().toISOString();
    const statements = products.map(p => ({
      sql: `
        INSERT OR REPLACE INTO products 
        (id, restaurant_id, name, price, category_id, image_url, description, is_active, 
         tracks_stock, current_stock, cost_per_unit, barcode, production_location_id,
         available_pdv, available_table, available_tablet, is_featured, sort_order, synced_at)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
      `,
      params: [
        p.id, p.restaurant_id, p.name, p.price, p.category_id, p.image_url, p.description,
        p.is_active ?? 1, p.tracks_stock ?? 0, p.current_stock, p.cost_per_unit, p.barcode,
        p.production_location_id, p.available_pdv ?? 1, p.available_table ?? 1, 
        p.available_tablet ?? 1, p.is_featured ?? 0, p.sort_order ?? 0, now
      ]
    }));
    
    transaction(statements);
  }, [isReady, transaction]);

  // ============ Categories ============

  const getCategories = useCallback((): LocalCategory[] => {
    if (!isReady || !restaurantId) return [];
    return query<LocalCategory>(
      `SELECT * FROM categories WHERE restaurant_id = ? AND is_active = 1 ORDER BY sort_order, name`,
      [restaurantId]
    );
  }, [isReady, query, restaurantId]);

  const upsertCategories = useCallback((categories: Partial<LocalCategory>[]): void => {
    if (!isReady || categories.length === 0) return;
    
    const now = new Date().toISOString();
    const statements = categories.map(c => ({
      sql: `
        INSERT OR REPLACE INTO categories 
        (id, restaurant_id, name, description, color, icon, sort_order, is_active, synced_at)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
      `,
      params: [c.id, c.restaurant_id, c.name, c.description, c.color, c.icon, c.sort_order ?? 0, c.is_active ?? 1, now]
    }));
    
    transaction(statements);
  }, [isReady, transaction]);

  // ============ Tables ============

  const getTables = useCallback((): LocalTable[] => {
    if (!isReady || !restaurantId) return [];
    return query<LocalTable>(
      `SELECT * FROM tables WHERE restaurant_id = ? AND is_active = 1 ORDER BY number`,
      [restaurantId]
    );
  }, [isReady, query, restaurantId]);

  const getTableByNumber = useCallback((number: string): LocalTable | null => {
    if (!isReady || !restaurantId) return null;
    // Normalize table number (handle leading zeros)
    const normalized = number.replace(/^0+/, '') || '0';
    const results = query<LocalTable>(
      `SELECT * FROM tables WHERE restaurant_id = ? AND (number = ? OR number = ?) AND is_active = 1`,
      [restaurantId, number, normalized]
    );
    return results[0] || null;
  }, [isReady, query, restaurantId]);

  const upsertTables = useCallback((tables: Partial<LocalTable>[]): void => {
    if (!isReady || tables.length === 0) return;
    
    const now = new Date().toISOString();
    const statements = tables.map(t => ({
      sql: `
        INSERT OR REPLACE INTO tables 
        (id, restaurant_id, number, name, status, current_order_id, grouped_to_order_id, service_tax_enabled, capacity, is_active, synced_at)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
      `,
      params: [t.id, t.restaurant_id, t.number, t.name, t.status ?? 'available', t.current_order_id, t.grouped_to_order_id ?? null, t.service_tax_enabled ?? 1, t.capacity ?? 4, t.is_active ?? 1, now]
    }));
    
    transaction(statements);
  }, [isReady, transaction]);

  const updateTableStatus = useCallback((id: string, status: string, currentOrderId?: string | null): void => {
    if (!isReady) return;
    execute(
      `UPDATE tables SET status = ?, current_order_id = ?, synced_at = NULL WHERE id = ?`,
      [status, currentOrderId ?? null, id]
    );
  }, [isReady, execute]);

  // ============ Commands ============

  const getCommands = useCallback((): LocalCommand[] => {
    if (!isReady || !restaurantId) return [];
    return query<LocalCommand>(
      `SELECT * FROM commands WHERE restaurant_id = ? AND is_active = 1 ORDER BY number`,
      [restaurantId]
    );
  }, [isReady, query, restaurantId]);

  const getCommandByNumber = useCallback((number: string): LocalCommand | null => {
    if (!isReady || !restaurantId) return null;
    const normalized = number.replace(/^0+/, '') || '0';
    const results = query<LocalCommand>(
      `SELECT * FROM commands WHERE restaurant_id = ? AND (number = ? OR number = ?) AND is_active = 1`,
      [restaurantId, number, normalized]
    );
    return results[0] || null;
  }, [isReady, query, restaurantId]);

  const upsertCommands = useCallback((commands: Partial<LocalCommand>[]): void => {
    if (!isReady || commands.length === 0) return;
    
    const now = new Date().toISOString();
    const statements = commands.map(c => ({
      sql: `
        INSERT OR REPLACE INTO commands 
        (id, restaurant_id, number, status, current_order_id, grouped_to_order_id, customer_name, service_tax_enabled, is_active, synced_at)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
      `,
      params: [c.id, c.restaurant_id, c.number, c.status ?? 'available', c.current_order_id, c.grouped_to_order_id ?? null, c.customer_name, c.service_tax_enabled ?? 1, c.is_active ?? 1, now]
    }));
    
    transaction(statements);
  }, [isReady, transaction]);

  const updateCommandStatus = useCallback((id: string, status: string, currentOrderId?: string | null, customerName?: string | null): void => {
    if (!isReady) return;
    execute(
      `UPDATE commands SET status = ?, current_order_id = ?, customer_name = ?, synced_at = NULL WHERE id = ?`,
      [status, currentOrderId ?? null, customerName ?? null, id]
    );
  }, [isReady, execute]);

  // ============ Payment Methods ============

  const getPaymentMethods = useCallback((): LocalPaymentMethod[] => {
    if (!isReady || !restaurantId) return [];
    return query<LocalPaymentMethod>(
      `SELECT * FROM payment_methods WHERE restaurant_id = ? AND is_active = 1 ORDER BY name`,
      [restaurantId]
    );
  }, [isReady, query, restaurantId]);

  const upsertPaymentMethods = useCallback((methods: Partial<LocalPaymentMethod>[]): void => {
    if (!isReady || methods.length === 0) return;
    
    const now = new Date().toISOString();
    const statements = methods.map(m => ({
      sql: `
        INSERT OR REPLACE INTO payment_methods 
        (id, restaurant_id, name, type, fee_percentage, is_active, accepts_change, synced_at)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?)
      `,
      params: [m.id, m.restaurant_id, m.name, m.type, m.fee_percentage ?? 0, m.is_active ?? 1, m.accepts_change ?? 0, now]
    }));
    
    transaction(statements);
  }, [isReady, transaction]);

  // ============ Production Locations ============

  const getProductionLocationById = useCallback((id: string): LocalProductionLocation | null => {
    if (!isReady || !id) return null;
    const results = query<LocalProductionLocation>(
      `SELECT * FROM production_locations WHERE id = ?`,
      [id]
    );
    return results[0] || null;
  }, [isReady, query]);

  const upsertProductionLocations = useCallback((locations: Partial<LocalProductionLocation>[]): void => {
    if (!isReady || locations.length === 0) return;

    const now = new Date().toISOString();
    const statements = locations.map(l => ({
      sql: `
        INSERT OR REPLACE INTO production_locations
        (id, restaurant_id, name, printer_name, is_active, synced_at)
        VALUES (?, ?, ?, ?, ?, ?)
      `,
      params: [l.id, l.restaurant_id, l.name, l.printer_name ?? null, l.is_active ?? 1, now]
    }));

    transaction(statements);
  }, [isReady, transaction]);

  // ============ Orders ============

  const getActiveOrders = useCallback((): LocalOrder[] => {
    if (!isReady || !restaurantId) return [];
    return query<LocalOrder>(
      `SELECT * FROM orders WHERE restaurant_id = ? AND status IN ('pending', 'preparing', 'ready') ORDER BY created_at DESC`,
      [restaurantId]
    );
  }, [isReady, query, restaurantId]);

  const getOrderByTableNumber = useCallback((tableNumber: string): LocalOrder | null => {
    if (!isReady || !restaurantId) return null;
    const normalized = tableNumber.replace(/^0+/, '') || '0';
    const results = query<LocalOrder>(
      `SELECT * FROM orders 
       WHERE restaurant_id = ? 
       AND (table_number = ? OR table_number = ?) 
       AND status IN ('pending', 'preparing', 'ready')
       ORDER BY created_at DESC LIMIT 1`,
      [restaurantId, tableNumber, normalized]
    );
    return results[0] || null;
  }, [isReady, query, restaurantId]);

  const getOrderByCommandNumber = useCallback((tabNumber: string): LocalOrder | null => {
    if (!isReady || !restaurantId) return null;
    const normalized = tabNumber.replace(/^0+/, '') || '0';
    const results = query<LocalOrder>(
      `SELECT * FROM orders 
       WHERE restaurant_id = ? 
       AND (tab_number = ? OR tab_number = ?) 
       AND status IN ('pending', 'preparing', 'ready')
       ORDER BY created_at DESC LIMIT 1`,
      [restaurantId, tabNumber, normalized]
    );
    return results[0] || null;
  }, [isReady, query, restaurantId]);

  const createOrder = useCallback((order: Omit<LocalOrder, 'synced' | 'server_id'>): string => {
    if (!isReady || !restaurantId) throw new Error('Database not ready');
    
    const id = order.id || crypto.randomUUID();
    
    execute(`
      INSERT INTO orders 
      (id, restaurant_id, order_number, order_type, table_number, tab_number, table_id, command_id,
       customer_name, subtotal, discount, total, status, created_at, synced, server_id)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 0, NULL)
    `, [
      id, restaurantId, order.order_number, order.order_type, order.table_number, order.tab_number,
      order.table_id, order.command_id, order.customer_name, order.subtotal, order.discount,
      order.total, order.status, order.created_at
    ]);

    // Add to sync queue
    addToSyncQueue('INSERT', 'orders', id, order, 10);
    
    return id;
  }, [isReady, execute, restaurantId]);

  const updateOrderTotals = useCallback((orderId: string, subtotal: number, discount: number, total: number): void => {
    if (!isReady) return;
    execute(
      `UPDATE orders SET subtotal = ?, discount = ?, total = ?, synced = 0 WHERE id = ?`,
      [subtotal, discount, total, orderId]
    );
    addToSyncQueue('UPDATE', 'orders', orderId, { subtotal, discount, total }, 5);
  }, [isReady, execute]);

  // ============ Order Items ============

  const getOrderItems = useCallback((orderId: string): LocalOrderItem[] => {
    if (!isReady) return [];
    return query<LocalOrderItem>(
      `SELECT * FROM order_items WHERE order_id = ? ORDER BY created_at`,
      [orderId]
    );
  }, [isReady, query]);

  const addOrderItem = useCallback((item: Omit<LocalOrderItem, 'synced' | 'server_id'>): string => {
    if (!isReady) throw new Error('Database not ready');
    
    const id = item.id || crypto.randomUUID();
    
    execute(`
      INSERT INTO order_items 
      (id, order_id, product_id, combo_id, product_name, quantity, unit_price, total_price, 
       notes, status, sent_to_kitchen, created_at, synced, server_id)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 0, NULL)
    `, [
      id, item.order_id, item.product_id, item.combo_id, item.product_name, item.quantity,
      item.unit_price, item.total_price, item.notes, item.status, item.sent_to_kitchen,
      item.created_at
    ]);

    // Add to sync queue with high priority
    addToSyncQueue('INSERT', 'order_items', id, item, 10);
    
    return id;
  }, [isReady, execute]);

  const updateOrderItemQuantity = useCallback((itemId: string, quantity: number, totalPrice: number): void => {
    if (!isReady) return;
    execute(
      `UPDATE order_items SET quantity = ?, total_price = ?, synced = 0 WHERE id = ?`,
      [quantity, totalPrice, itemId]
    );
    addToSyncQueue('UPDATE', 'order_items', itemId, { quantity, total_price: totalPrice }, 5);
  }, [isReady, execute]);

  const deleteOrderItem = useCallback((itemId: string): void => {
    if (!isReady) return;
    execute(`DELETE FROM order_items WHERE id = ?`, [itemId]);
    addToSyncQueue('DELETE', 'order_items', itemId, undefined, 5);
  }, [isReady, execute]);

  /**
   * Mark order items as printed locally and enqueue UPDATE sync ops so the
   * server-side `generate-print-jobs` skips them (only processes items
   * where `printed_at IS NULL`). Priority 9 so the UPDATE follows the
   * INSERT (priority 10) closely on reconnect.
   */
  const markLocalOrderItemsPrinted = useCallback((itemIds: string[], printedAtIso: string): void => {
    if (!isReady || itemIds.length === 0) return;
    for (const id of itemIds) {
      execute(
        `UPDATE order_items SET printed_at = ?, synced = 0 WHERE id = ?`,
        [printedAtIso, id]
      );
      addToSyncQueue('UPDATE', 'order_items', id, { printed_at: printedAtIso }, 9);
    }
  }, [isReady, execute]);

  // ============ Order Payments ============

  const addOrderPayment = useCallback((payment: Omit<LocalOrderPayment, 'synced' | 'server_id'>): string => {
    if (!isReady) throw new Error('Database not ready');
    
    const id = payment.id || crypto.randomUUID();
    
    execute(`
      INSERT INTO order_payments 
      (id, order_id, payment_method_id, payment_method_name, amount, fee_amount, created_at, synced, server_id)
      VALUES (?, ?, ?, ?, ?, ?, ?, 0, NULL)
    `, [
      id, payment.order_id, payment.payment_method_id, payment.payment_method_name,
      payment.amount, payment.fee_amount, payment.created_at
    ]);

    // Add to sync queue with high priority
    addToSyncQueue('INSERT', 'order_payments', id, payment, 8);
    
    return id;
  }, [isReady, execute]);

  const getOrderPayments = useCallback((orderId: string): LocalOrderPayment[] => {
    if (!isReady) return [];
    return query<LocalOrderPayment>(
      `SELECT * FROM order_payments WHERE order_id = ? ORDER BY created_at`,
      [orderId]
    );
  }, [isReady, query]);

  // ============ Close Order (Offline) ============

  const closeOrderOffline = useCallback((
    orderId: string, 
    status: string,
    serviceTax: number,
    total: number,
    tableId?: string | null,
    commandId?: string | null,
    discount?: number,
    couvertQuantity?: number,
    couvertAmount?: number,
    paymentMethodId?: string | null,
    paymentFee?: number,
    peopleCount?: number
  ): void => {
    if (!isReady) return;

    const now = new Date().toISOString();
    
    // Build the sync payload with CORRECT column names matching the Supabase orders table
    const syncPayload: Record<string, unknown> = {
      status,
      additional_fees: serviceTax, // CORRECT: was incorrectly 'service_tax'
      total,
      updated_at: now, // CORRECT: was incorrectly 'closed_at'
      discount: discount ?? 0,
      couvert_quantity: couvertQuantity ?? 0,
      couvert_amount: couvertAmount ?? 0,
      people_count: peopleCount ?? 1,
    };

    // Only include payment fields if provided
    if (paymentMethodId) {
      syncPayload.payment_method_id = paymentMethodId;
    }
    if (paymentFee !== undefined && paymentFee > 0) {
      syncPayload.payment_fee = paymentFee;
    }

    // Update order status in local SQLite with ALL financial fields
    execute(
      `UPDATE orders SET status = ?, additional_fees = ?, service_tax = ?, total = ?, discount = ?, couvert_quantity = ?, couvert_amount = ?, people_count = ?, updated_at = ?, synced = 0 WHERE id = ?`,
      [status, serviceTax, serviceTax, total, discount ?? 0, couvertQuantity ?? 0, couvertAmount ?? 0, peopleCount ?? 1, now, orderId]
    );
    addToSyncQueue('UPDATE', 'orders', orderId, syncPayload, 8);

    // Update table status
    if (tableId) {
      execute(
        `UPDATE tables SET status = 'available', current_order_id = NULL, name = NULL, synced_at = NULL WHERE id = ?`,
        [tableId]
      );
      addToSyncQueue('UPDATE', 'tables', tableId, { status: 'available', current_order_id: null, name: null }, 3);
    }

    // Update command status
    if (commandId) {
      execute(
        `UPDATE commands SET status = 'available', current_order_id = NULL, customer_name = NULL, synced_at = NULL WHERE id = ?`,
        [commandId]
      );
      addToSyncQueue('UPDATE', 'commands', commandId, { status: 'available', current_order_id: null, customer_name: null }, 3);
    }
  }, [isReady, execute]);

  // ============ Sync Queue Stats ============

  const getSyncQueueCounts = useCallback((): { orders: number; items: number; payments: number; closures: number; total: number } => {
    if (!isReady) return { orders: 0, items: 0, payments: 0, closures: 0, total: 0 };
    
    const result = query<{ table_name: string; count: number }>(`
      SELECT table_name, COUNT(*) as count 
      FROM sync_queue 
      WHERE status IN ('pending', 'syncing')
      GROUP BY table_name
    `);
    
    const counts = { orders: 0, items: 0, payments: 0, closures: 0, total: 0 };
    for (const row of result) {
      if (row.table_name === 'orders') counts.orders = row.count;
      else if (row.table_name === 'order_items') counts.items = row.count;
      else if (row.table_name === 'order_payments') counts.payments = row.count;
      counts.total += row.count;
    }
    // closures = orders with status update to 'delivered'
    const closuresResult = query<{ count: number }>(`
      SELECT COUNT(*) as count FROM sync_queue 
      WHERE status IN ('pending', 'syncing') 
      AND table_name = 'orders' 
      AND operation = 'UPDATE'
      AND payload LIKE '%delivered%'
    `);
    counts.closures = closuresResult[0]?.count || 0;
    
    return counts;
  }, [isReady, query]);

  // ============ Active Consumptions (Offline Cache) ============

  interface ActiveConsumptionData {
    id: string;
    type: 'table' | 'command';
    restaurant_id: string;
    order_id: string;
    order_number: number | null;
    number: string;
    items_json: string;
    updated_at: string;
  }

  const upsertActiveConsumption = useCallback((
    id: string,
    type: 'table' | 'command',
    orderId: string,
    orderNumber: number | null,
    number: string,
    items: LocalOrderItem[]
  ): void => {
    if (!isReady || !restaurantId) return;
    
    execute(`
      INSERT OR REPLACE INTO active_consumptions 
      (id, type, restaurant_id, order_id, order_number, number, items_json, updated_at)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?)
    `, [id, type, restaurantId, orderId, orderNumber, number, JSON.stringify(items), new Date().toISOString()]);
    
    console.log(`[SQLiteLocal] Cached active consumption: ${type} ${number}, order ${orderId}, ${items.length} items`);
  }, [isReady, execute, restaurantId]);

  const getActiveConsumption = useCallback((id: string): { orderId: string; orderNumber: number | null; items: LocalOrderItem[] } | null => {
    if (!isReady) return null;
    
    const results = query<ActiveConsumptionData>(`SELECT * FROM active_consumptions WHERE id = ?`, [id]);
    if (results[0]) {
      return {
        orderId: results[0].order_id,
        orderNumber: results[0].order_number,
        items: JSON.parse(results[0].items_json || '[]'),
      };
    }
    return null;
  }, [isReady, query]);

  const getActiveConsumptionByOrderId = useCallback((orderId: string): { id: string; type: string; items: LocalOrderItem[] } | null => {
    if (!isReady) return null;
    
    const results = query<ActiveConsumptionData>(`SELECT * FROM active_consumptions WHERE order_id = ?`, [orderId]);
    if (results[0]) {
      return {
        id: results[0].id,
        type: results[0].type,
        items: JSON.parse(results[0].items_json || '[]'),
      };
    }
    return null;
  }, [isReady, query]);

  const deleteActiveConsumption = useCallback((id: string): void => {
    if (!isReady) return;
    execute(`DELETE FROM active_consumptions WHERE id = ?`, [id]);
    console.log(`[SQLiteLocal] Removed active consumption cache: ${id}`);
  }, [isReady, execute]);

  const getAllActiveConsumptions = useCallback((): { id: string; type: string; orderId: string; orderNumber: number | null; number: string; itemsCount: number }[] => {
    if (!isReady || !restaurantId) return [];
    
    const results = query<ActiveConsumptionData>(
      `SELECT * FROM active_consumptions WHERE restaurant_id = ?`,
      [restaurantId]
    );
    
    return results.map(r => ({
      id: r.id,
      type: r.type,
      orderId: r.order_id,
      orderNumber: r.order_number,
      number: r.number,
      itemsCount: JSON.parse(r.items_json || '[]').length,
    }));
  }, [isReady, query, restaurantId]);

  const getAllActiveConsumptionsWithItems = useCallback((): { id: string; type: string; orderId: string; orderNumber: number | null; number: string; updatedAt: string; items: LocalOrderItem[] }[] => {
    if (!isReady || !restaurantId) return [];
    
    const results = query<ActiveConsumptionData>(
      `SELECT * FROM active_consumptions WHERE restaurant_id = ?`,
      [restaurantId]
    );
    
    return results.map(r => ({
      id: r.id,
      type: r.type,
      orderId: r.order_id,
      orderNumber: r.order_number,
      number: r.number,
      updatedAt: r.updated_at,
      items: JSON.parse(r.items_json || '[]') as LocalOrderItem[],
    }));
  }, [isReady, query, restaurantId]);

  // ============ Utilities ============

  const clearRestaurantData = useCallback((): void => {
    if (!isReady || !restaurantId) return;
    
    transaction([
      { sql: `DELETE FROM order_payments WHERE order_id IN (SELECT id FROM orders WHERE restaurant_id = ?)`, params: [restaurantId] },
      { sql: `DELETE FROM order_items WHERE order_id IN (SELECT id FROM orders WHERE restaurant_id = ?)`, params: [restaurantId] },
      { sql: `DELETE FROM orders WHERE restaurant_id = ?`, params: [restaurantId] },
      { sql: `DELETE FROM products WHERE restaurant_id = ?`, params: [restaurantId] },
      { sql: `DELETE FROM categories WHERE restaurant_id = ?`, params: [restaurantId] },
      { sql: `DELETE FROM tables WHERE restaurant_id = ?`, params: [restaurantId] },
      { sql: `DELETE FROM commands WHERE restaurant_id = ?`, params: [restaurantId] },
      { sql: `DELETE FROM payment_methods WHERE restaurant_id = ?`, params: [restaurantId] },
      { sql: `DELETE FROM production_locations WHERE restaurant_id = ?`, params: [restaurantId] },
      { sql: `DELETE FROM active_consumptions WHERE restaurant_id = ?`, params: [restaurantId] },
    ]);
  }, [isReady, transaction, restaurantId]);

  return {
    isReady,
    // Products
    getProducts,
    getProductById,
    getProductByBarcode,
    upsertProducts,
    // Categories
    getCategories,
    upsertCategories,
    // Tables
    getTables,
    getTableByNumber,
    upsertTables,
    updateTableStatus,
    // Commands
    getCommands,
    getCommandByNumber,
    upsertCommands,
    updateCommandStatus,
    // Payment Methods
    getPaymentMethods,
    upsertPaymentMethods,
    // Production Locations
    getProductionLocationById,
    upsertProductionLocations,
    // Orders
    getActiveOrders,
    getOrderByTableNumber,
    getOrderByCommandNumber,
    createOrder,
    updateOrderTotals,
    // Order Items
    getOrderItems,
    addOrderItem,
    updateOrderItemQuantity,
    deleteOrderItem,
    markLocalOrderItemsPrinted,
    // Order Payments
    addOrderPayment,
    getOrderPayments,
    closeOrderOffline,
    // Active Consumptions (Offline Cache)
    upsertActiveConsumption,
    getActiveConsumption,
    getActiveConsumptionByOrderId,
    deleteActiveConsumption,
    getAllActiveConsumptions,
    getAllActiveConsumptionsWithItems,
    // Sync Stats
    getSyncQueueCounts,
    // Utilities
    clearRestaurantData,
  };
}
